import { h, Component } from 'preact';
import cx from 'classnames';
import Axios from 'axios';

import GoogleMapsLoader from 'google-maps';
import { GOOGLE_MAP_API_KEY, PROPERTY_PAGE_MARKER_ICON, DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE, GEOCODING_KEY } from '../../../../constants/maps';

import { Form, Field, FieldList } from '../../../utils/forms/form';
import * as Validators from '../../../utils/forms/validators';

import { DivCat, Location, Listing } from '../../../../types';
import { LocationSearchBox } from '../common/location-search.component';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../utils/forms/hoppler-linkstate';
import { hopplerApi } from '../../../../constants';
import { CATEGORY_RENT, CATEGORY_SALE, DIVISION_RESIDENTIAL, DIVISION_COMMERCIAL } from '../post-property.component';
import { PropertyUtils } from '../../../../core/property-utils';
import { MapUtils } from '../../../utils/map-utils';
import { LocationRequestModal } from './location-request-modal.component';

interface PostPropertyLocationProps {
  addreNoLabel?: string;
  searchFieldLabel?: string;
  requiresBuilding?: boolean;
  currentDivCat?: DivCat;

  onLocationChange?: (location: Location) => any;
  currentLocation?: Location;

  onUpdateFormFields: (form: Form) => any;
  onDupeCheck: (hasDupe: boolean) => any;

  isPostProperty?: boolean;
  listing?: Listing; // Used for editing an existing listing's lat/long
  canEdit?: boolean;
}

interface PostPropertyLocationState {
  currentLocation?: Location;
  map?: any;

  locationForm?: Form;

  hasDuplicate?: boolean;

  locationRequestDialogVisible?: boolean;
}

export class PostPropertyLocation extends Component<PostPropertyLocationProps, PostPropertyLocationState> {
  locationSearchValidation: Function;
  setLocationSearchDivCat: Function;
  clearLocation: Function;

  form$: Subject<Form>;
  duplicateCheck$: Subject<any>;

  private subscriptions: Subscription[] = [];
  propertyUtils = new PropertyUtils();
  mapUtils = new MapUtils();

  constructor(props: PostPropertyLocationProps) {
    super(props);

    this.setupForm();

    if (props.currentLocation) {
      this.setState({
        currentLocation: props.currentLocation
      });

      Validators.validateForm(this.state.locationForm);
    }
  }

  addreNameValidator: Validators.Validator = (field, form) => {
    if (!this.props.requiresBuilding) {
      if (!field.value) {
        field.isValid = false;
        form.isValid = false;
        return false;
      }
    }
  }

  addreNoValidator: Validators.Validator =  (field, form) => {
    if (!field.value) {
      field.isValid = false;
      form.isValid = false;
      return false;
    } else {
      this.duplicateCheck$.next(null);
    }
  }

  checkDuplicate(locationName: string, addreNo: string, currentLocation: Location) {
    const data = {
      addreName: locationName,
      addreNo: addreNo,
      category: this.props.currentDivCat.category == 'Rent' ? CATEGORY_RENT : CATEGORY_SALE,
      division: this.props.currentDivCat.division == 'Residential' ? DIVISION_RESIDENTIAL : DIVISION_COMMERCIAL,

      tower: this.state.locationForm.fields['tower'].value,

      addreArea: currentLocation.area,
      addreCity: currentLocation.city
    }

    return Axios.post(
      `${hopplerApi.host}/api/properties/check-duplicate`,
      data,
      {
        withCredentials: true
      }
    );
  }

  setupForm() {
    let fields: FieldList = {
      'addreNo': new Field(
        [],
        [ this.addreNoValidator ],
        this.props.currentLocation && this.props.currentLocation.unitNo ? this.props.currentLocation.unitNo : null
      ),
      'tower': new Field(
        [],
        null,
        this.props.currentLocation && this.props.currentLocation.tower ? this.props.currentLocation.tower : null
      ),
      'addreName': new Field(
        [],
        [ this.addreNameValidator ],
        this.getAddreNamePrefill(this.props)
      ),
      'streetName': new Field(
        [ (!this.props.requiresBuilding ? Validators.required : '') ],
        null,
        this.props.currentLocation && this.props.currentLocation.streetName ? this.props.currentLocation.streetName : null
      ),

      'latitude': new Field(
        [],
        null
      ),
      'longitude': new Field(
        [],
        null
      )
    };

    this.form$ = new Subject<Form>();
    this.setState({
      locationForm: new Form(fields)
    });

    if (this.props.currentLocation) {
      this.props.onUpdateFormFields(this.state.locationForm);
    }

    this.subscriptions.push(
      this.form$.pipe(distinctUntilChanged())
        .subscribe(form => {

          Validators.validateForm(form);

          let state = {};
          state['locationForm'] = form;
          this.setState(state);

          // REFACTOR NOTE
          // Lat/long data is sent out through this, and not through
          // onLocationChange
          this.props.onUpdateFormFields(form);
        })
    );

    this.duplicateCheck$ = new Subject<any>();
    this.subscriptions.push(
      this.duplicateCheck$.pipe()
        .subscribe(dupeLocationData => {

          if (this.state.currentLocation) {
            const locationName = ( 
              this.state.locationForm.fields['addreName'].value || 
              this.state.currentLocation.name
            );
  
            const addreNo = this.state.locationForm.fields['addreNo'].value;
  
            let promise = this.checkDuplicate(locationName, addreNo, this.state.currentLocation);
  
            promise.then((res) => {
              this.setState({
                hasDuplicate: false
              });

              this.props.onDupeCheck(false);
            }, (err) => {
              this.setState({
                hasDuplicate: true
              });

              this.props.onDupeCheck(true);
            })
          }

        })
    );

  }

  componentWillUnmount() {
    this.releaseMap();
    this.subscriptions.forEach(s => s.unsubscribe())
  }

  componentDidMount() {
    this.loadMap();
  }

  getAddreNamePrefill(props: PostPropertyLocationProps) {
    if (this.props.currentLocation) {
      if (this.props.currentLocation.name) {
        return this.props.currentLocation.name;
      }

      if (this.props.currentLocation.streetName) {
        return this.props.currentLocation.streetName;
      }
    }

    return null;
  }

  onLocationChange = (location: Location) => {
    // console.log('PostPropertyLocation: onLocationChange', location);
    if (!this.state.locationForm.fields['addreName'].value) {
      hopplerUpdateFormFieldValue(
        this.form$,
        this.state.locationForm,
        'addreName',
        location.label
      );
    }

    location.unitNo = this.state.locationForm.fields['addreNo'].value;
    location.tower = this.state.locationForm.fields['tower'].value;
    location.streetName = this.state.locationForm.fields['streetName'].value;

    this.setState({
      currentLocation: location
    });

    this.duplicateCheck$.next(null);
    this.props.onLocationChange(location);

    this.loadMap();
  }

  releaseMap = () => {
    GoogleMapsLoader.release(function() {
      
    });
  }

  getSearchExcludes(props: PostPropertyLocationProps): string {
    if (props.requiresBuilding) {
      return 'regions,cities,areas,villages';
    } else {
      return 'regions,cities,villages,buildings';
    }
  }

  setClearLocation = (passedFunction: Function) => {
    this.clearLocation = passedFunction;
  }

  setLocationDivCat = (passedFunction: Function) => {
    this.setLocationSearchDivCat = passedFunction;
  }

  setLocationValidation = (passedValidation: Function) => {
    this.locationSearchValidation = passedValidation;
  }

  getLocationTower(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    if (props.requiresBuilding || props.currentDivCat.type === 'Building' ||
        props.currentDivCat.type === 'Resort' ||
        props.currentDivCat.type === 'Warehouse' ) {
      let form = state.locationForm;
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Tower name</label>
              <input className="form-control hi-text-input" 
                disabled={ !props.canEdit }
                placeholder="Tower name" 
                type="text"
                value={form.fields['tower'].value}
                onInput={ hopplerLinkState(this.form$, form, 'tower') }/>
            </div>
          </div>
        </div>
      );
    }
  }

  getLocationStreet(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    if (!props.requiresBuilding) {
      let form = state.locationForm;
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Street name*</label>
              <input 
                disabled={ !props.canEdit }
                className={ cx('form-control hi-text-input',
                  {
                    'invalid': Validators.cxValidateField(form.fields['streetName'])
                  })
                }   
                placeholder="Street name" 
                type="text"
                value={form.fields['streetName'].value}
                onInput={ hopplerLinkState(this.form$, form, 'streetName') }/>
            </div>
          </div>
        </div>
      );
    }
  }

  setupMapEventHandlers() {

  }

  loadMap() {
    GoogleMapsLoader.VERSION = 'weekly';
    GoogleMapsLoader.KEY = GOOGLE_MAP_API_KEY;

    let center, marker, map;
    let lat: number, lng: number;
    let zoom = 18; // Reasonably zoomed-in, for when Google manages to give a lat/lng

    const existingListing = this.props.listing;

    GoogleMapsLoader.load(async (google) => {
      const currentLocation = this.state.currentLocation;
      if (this.state.currentLocation || existingListing) {
        // the API doesn't keep coords of locations at this time.
        // Get these from Google, and point the map there.

        if (existingListing) {
          lat = existingListing.latitude;
          lng = existingListing.longitude;
        } else if (currentLocation.latitude && currentLocation.longitude) {
          lat = currentLocation.latitude;
          lng = currentLocation.longitude;
        } else {
          const res = await this.mapUtils.geocodeLocation(this.state.currentLocation);

          if (res.data.results.length > 0) {
            const geoLocation = res.data.results[0].geometry.location;
            lat = geoLocation.lat;
            lng = geoLocation.lng; 
          } else {
            // Google couldn't find the given location -- fall back to defaults
            lat = DEFAULT_MAP_LATITUDE;
            lng = DEFAULT_MAP_LONGITUDE;
            // Start zoomed-out a bit, so it's easier to move the marker to the
            // right spot if it's in the other end of the country
            zoom = 10;
          }
        }
        
        center = new google.maps.LatLng(lat, lng);
        map = new google.maps.Map(
          document.getElementById('hiMap'),
          { center, zoom }
        );

      } else {
        let center = new google.maps.LatLng(DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE);
        lat = DEFAULT_MAP_LATITUDE;
        lng = DEFAULT_MAP_LONGITUDE;
  
        let map = new google.maps.Map(
          document.getElementById('hiMap'),
          {
            center: center,
            zoom: 14
          }
        );
  
        this.setState({
          map: map
        });
      }

      marker = new google.maps.Marker({
        position: {
          lat: lat, 
          lng: lng
        },
        opacity: 1,
        icon: PROPERTY_PAGE_MARKER_ICON,
        draggable: !this.propertyUtils.doesTypeRequireBuilding(this.props.currentDivCat)
      });

      google.maps.event.addListener(marker, 'dragend', (evt) => {
        // console.log('SELECTED LATLNG:', evt.latLng.lat(), evt.latLng.lng());
        hopplerUpdateFormFieldValue(
          this.form$,
          this.state.locationForm,
          'latitude',
          evt.latLng.lat()
        );
        hopplerUpdateFormFieldValue(
          this.form$,
          this.state.locationForm,
          'longitude',
          evt.latLng.lng()
        );
      });
  
      hopplerUpdateFormFieldValue(
        this.form$,
        this.state.locationForm,
        'latitude',
        lat
      );
      hopplerUpdateFormFieldValue(
        this.form$,
        this.state.locationForm,
        'longitude',
        lng
      );
  
      marker.setMap(map);
  
      this.setState({
        map: map
      });
    });
  }

  getLocationMap(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    return (
      <div className={ cx('row mt-3 ', {
        'd-none': !state.map
      }) }>
        <div className="col-12">
          <div className="hi-postprop__tab-content__location__map-container">
            <div className="" id="hiMap" style="height:100%; width: 100%;">

            </div>
          </div>
        </div>
      </div>
    );
  }

  getInitialLocationInputText(state: PostPropertyLocationState) {
    if (!state.currentLocation) {
      return;
    }

    if (state.currentLocation.name) {
      return state.currentLocation.name;
    }

    if (state.currentLocation.building) {
      return state.currentLocation.building;
    }

    if (state.currentLocation.area) {
      return state.currentLocation.area;
    }
  }

  getUnitNumberBlock(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    const form = state.locationForm;

    if (props.listing && !props.listing.canEdit) {
      return null
    }

    return (
      <div className="row">
        <div className="col-12">
          <div className="hi-input-group">
            <label className="hi-input-group__label">{props.addreNoLabel}*</label>
            <input 
              disabled={ !props.canEdit }
              className={ cx('form-control hi-text-input',
                {
                  'invalid': Validators.cxValidateField(form.fields['addreNo'])
                })
              }  
              placeholder={props.addreNoLabel} 
              type="text"
              value={form.fields['addreNo'].value}
              onInput={ hopplerLinkState(this.form$, form, 'addreNo') }/>
          </div>
        </div>
        <div className="col-12">
          <div className="hi-postprop__tab-content__note">
            <strong>Note</strong>: To prevent duplicate listings, please specify {props.addreNoLabel}. Rest assured, other people won't see the {props.addreNoLabel}.
          </div>
        </div>
      </div>
    );
  }

  getLocationRequestDialog(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    if (state.locationRequestDialogVisible) {
      return <LocationRequestModal
        consumerMode={ true }
        divCat={ props.currentDivCat }
        close={ () => this.setState({ locationRequestDialogVisible: false }) }
        onLocationDone={ (location) => this.onLocationChange(location) }
      />
    }
  }

  render(props: PostPropertyLocationProps, state: PostPropertyLocationState) {
    let form = state.locationForm;

    return (
      <div className={ cx('hi-postprop__tab-content__location', {
            'post-property': props.isPostProperty
          }) }>
        { this.getUnitNumberBlock(props, state) }

        { this.getLocationTower(props, state) }
        { this.getLocationStreet(props, state) }

        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">{ props.searchFieldLabel }*</label>

              <LocationSearchBox 
                divCat={ props.currentDivCat }
                initialText={ this.getInitialLocationInputText(state) }
                onLocationSelected={this.onLocationChange} 
                setValidation={this.setLocationValidation}
                setClearLocation={this.setClearLocation}
                exclude={this.getSearchExcludes(props)}
                disabled={ !props.canEdit }
                
                extraItemText='Request new location...'
                onExtraItemClick={ () => this.setState({ locationRequestDialogVisible: true }) } />

              { this.getLocationRequestDialog(props, state) }
            </div>
          </div>
        </div>

        { this.getLocationMap(props, state) }

      </div>
    );
  }
}